"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.onINP = exports.INPThresholds = void 0;

var _types = require("../../types.js");

var _bindReporter = require("./lib/bindReporter.js");

var _initMetric = require("./lib/initMetric.js");

var _interactions = require("./lib/interactions.js");

var _observe = require("./lib/observe.js");

var _onHidden = require("./lib/onHidden.js");

var _interactionCountPolyfill = require("./lib/polyfills/interactionCountPolyfill.js");

var _whenActivated = require("./lib/whenActivated.js");

var _whenIdle = require("./lib/whenIdle.js");

/*
 * Copyright 2022 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/** Thresholds for INP. See https://web.dev/articles/inp#what_is_a_good_inp_score */
const INPThresholds = exports.INPThresholds = [200, 500];
/**
 * Calculates the [INP](https://web.dev/articles/inp) value for the current
 * page and calls the `callback` function once the value is ready, along with
 * the `event` performance entries reported for that interaction. The reported
 * value is a `DOMHighResTimeStamp`.
 *
 * A custom `durationThreshold` configuration option can optionally be passed to
 * control what `event-timing` entries are considered for INP reporting. The
 * default threshold is `40`, which means INP scores of less than 40 are
 * reported as 0. Note that this will not affect your 75th percentile INP value
 * unless that value is also less than 40 (well below the recommended
 * [good](https://web.dev/articles/inp#what_is_a_good_inp_score) threshold).
 *
 * If the `reportAllChanges` configuration option is set to `true`, the
 * `callback` function will be called as soon as the value is initially
 * determined as well as any time the value changes throughout the page
 * lifespan.
 *
 * _**Important:** INP should be continually monitored for changes throughout
 * the entire lifespan of a page—including if the user returns to the page after
 * it's been hidden/backgrounded. However, since browsers often [will not fire
 * additional callbacks once the user has backgrounded a
 * page](https://developer.chrome.com/blog/page-lifecycle-api/#advice-hidden),
 * `callback` is always called when the page's visibility state changes to
 * hidden. As a result, the `callback` function might be called multiple times
 * during the same page load._
 */

const onINP = (onReport, opts = {}) => {
  // Return if the browser doesn't support all APIs needed to measure INP.
  if (!('PerformanceEventTiming' in _types.WINDOW && 'interactionId' in PerformanceEventTiming.prototype)) {
    return;
  }

  (0, _whenActivated.whenActivated)(() => {
    // TODO(philipwalton): remove once the polyfill is no longer needed.
    (0, _interactionCountPolyfill.initInteractionCountPolyfill)();
    const metric = (0, _initMetric.initMetric)('INP'); // eslint-disable-next-line prefer-const

    let report;

    const handleEntries = entries => {
      // Queue the `handleEntries()` callback in the next idle task.
      // This is needed to increase the chances that all event entries that
      // occurred between the user interaction and the next paint
      // have been dispatched. Note: there is currently an experiment
      // running in Chrome (EventTimingKeypressAndCompositionInteractionId)
      // 123+ that if rolled out fully may make this no longer necessary.
      (0, _whenIdle.whenIdle)(() => {
        entries.forEach(_interactions.processInteractionEntry);
        const inp = (0, _interactions.estimateP98LongestInteraction)();

        if (inp && inp.latency !== metric.value) {
          metric.value = inp.latency;
          metric.entries = inp.entries;
          report();
        }
      });
    };

    const po = (0, _observe.observe)('event', handleEntries, {
      // Event Timing entries have their durations rounded to the nearest 8ms,
      // so a duration of 40ms would be any event that spans 2.5 or more frames
      // at 60Hz. This threshold is chosen to strike a balance between usefulness
      // and performance. Running this callback for any interaction that spans
      // just one or two frames is likely not worth the insight that could be
      // gained.
      durationThreshold: opts.durationThreshold != null ? opts.durationThreshold : _interactions.DEFAULT_DURATION_THRESHOLD
    });
    report = (0, _bindReporter.bindReporter)(onReport, metric, INPThresholds, opts.reportAllChanges);

    if (po) {
      // Also observe entries of type `first-input`. This is useful in cases
      // where the first interaction is less than the `durationThreshold`.
      po.observe({
        type: 'first-input',
        buffered: true
      });
      (0, _onHidden.onHidden)(() => {
        handleEntries(po.takeRecords());
        report(true);
      });
    }
  });
};

exports.onINP = onINP;